import { useCallback, useEffect, useState, useRef } from 'react'
import { flushSync } from 'react-dom'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useWebSocket, useStreamingMessages } from '@/hooks/useWebSocket'
import { messageStreamStarted } from '@/store/slices/socketSlice'
import { 
  setActiveSession,
  addOptimisticMessage,
  removeOptimisticMessage,
  startStreamingMessage,
  updateStreamingMessage,
  streamingError,
  cancelStreaming,
  sendChatMessage,
  loadChatSession,
  fetchChatSessions
} from '@/store/slices/hrChatSlice'
import { HRMessage } from '@/types/chat.types'
import { v4 as uuidv4 } from 'uuid'
import { transferStreamingMessages } from '@/store/slices/socketSlice'

interface UseHRChatStreamingOptions {
  autoConnect?: boolean
}

export const useHRChatStreaming = (options: UseHRChatStreamingOptions = {}) => {
  const { autoConnect = true } = options
  const dispatch = useAppDispatch()
  
  // Redux state
  const {
    sessions,
    activeSessionId,
    preferences,
    streaming,
    loadingSessions,
    sessionsError
  } = useAppSelector(state => state.hrChat)
  
  // joinedSessions not needed anymore - session switching handled by chat:session-created event
  
  const activeSession = activeSessionId ? sessions[activeSessionId] : null
  
  // WebSocket hooks
  const webSocket = useWebSocket({ 
    autoConnect, 
    sessionId: activeSessionId || undefined 
  })
  
  const streamingMessages = useStreamingMessages(activeSessionId || '')
  
  // Local state
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize WebSocket connection and load initial data
  useEffect(() => {
    const initialize = async () => {
      try {
        // Connect WebSocket first
        if (autoConnect) {
          await webSocket.connect()
        }
        
        // Load chat sessions
        await dispatch(fetchChatSessions(10)).unwrap()
        setIsInitialized(true)
      } catch (error) {
        console.error('Failed to initialize HR chat:', error)
        setIsInitialized(true) // Set to true even on error to prevent infinite loops
      }
    }

    if (!isInitialized) {
      initialize()
    }
  }, [dispatch, isInitialized, autoConnect])

  // Auto-join session when active session changes (but not for temp sessions)
  useEffect(() => {
    if (activeSessionId && 
        !activeSessionId.startsWith('temp-session-') && 
        webSocket.isConnected()) {
      webSocket.joinSession(activeSessionId)
    }
  }, [activeSessionId]) // Remove webSocket from dependencies

  // Session switching is now handled directly by the chat:session-created event in useWebSocket
  // No need for complex logic here anymore!

  // Handle streaming events from socket - simplified to prevent loops
  const processedMessagesRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!activeSessionId) return

    streamingMessages.streamingMessages.forEach(streamMsg => {
      const messageKey = `${streamMsg.sessionId}-${streamMsg.id}`
      
      if (streamMsg.sessionId === activeSessionId && !processedMessagesRef.current.has(messageKey)) {
        processedMessagesRef.current.add(messageKey)
        
        dispatch(updateStreamingMessage({
          sessionId: activeSessionId,
          messageId: streamMsg.id,
          content: streamMsg.content,
          isComplete: streamMsg.isComplete,
          citations: streamMsg.citations.map(citation => ({
            id: `citation-${Date.now()}-${Math.random()}`,
            chunkId: citation.documentId,
            relevanceScore: citation.relevanceScore,
            wasUsed: true,
            citationText: citation.snippet,
            rankOrder: 1,
            chunk: {
              id: citation.documentId,
              text: citation.snippet,
              document: {
                id: citation.documentId,
                title: citation.documentTitle,
                filename: citation.documentTitle,
                fileType: 'pdf'
              }
            }
          }))
        }))
      }
    })
  }, [streamingMessages.streamingMessages.length, activeSessionId]) // Only re-run when message count changes

  // Send a message with streaming support
  const sendMessage = useCallback(async (content: string, sessionId?: string | null) => {
    try {
      const targetSessionId = sessionId || activeSessionId
      const messageId = uuidv4()
      const userMessageId = uuidv4()

      // If no session, create a temporary one
      let workingSessionId = targetSessionId || `temp-session-${Date.now()}`
      if (!targetSessionId) {
        dispatch(setActiveSession(workingSessionId))
      }

      // Add optimistic user message
      const userMessage: HRMessage = {
        id: userMessageId,
        role: 'USER',
        content,
        createdAt: new Date().toISOString()
      }

      dispatch(addOptimisticMessage({ 
        sessionId: workingSessionId, 
        message: userMessage 
      }))

      // Check if streaming is enabled and WebSocket is connected
      if (streaming.enabled && webSocket.isConnected()) {
        // Use flushSync to ensure both streaming states are set synchronously
        // This bypasses React 18's automatic batching to show loading indicator immediately
        flushSync(() => {
          // Start streaming response
          dispatch(startStreamingMessage({ 
            sessionId: workingSessionId, 
            messageId 
          }))

          // IMMEDIATELY create streaming message in socketSlice for hasActiveStreams
          dispatch(messageStreamStarted({
            messageId,
            sessionId: workingSessionId,
            timestamp: new Date()
          }))
        })

        // Send message via WebSocket
        console.log('🚀 Sending WebSocket message:', { sessionId: workingSessionId, content: content.slice(0, 50) + '...' })
        console.log('🎯 Current activeSessionId when sending:', activeSessionId)
        webSocket.sendMessage({
          sessionId: workingSessionId,
          content,
          role: 'USER'
        })

      } else {
        // Fallback to traditional HTTP request
        try {
          const response = await dispatch(sendChatMessage({
            content,
            sessionId: targetSessionId || null,
            preferences
          })).unwrap()

          // Remove optimistic message since the thunk adds the real ones
          dispatch(removeOptimisticMessage({ 
            sessionId: workingSessionId, 
            messageId: userMessageId 
          }))

          // Update active session if it's a new session
          if (response.isNewSession && response.sessionId) {
            dispatch(setActiveSession(response.sessionId))
          }
        } catch (error) {
          // Remove optimistic message on error
          dispatch(removeOptimisticMessage({ 
            sessionId: workingSessionId, 
            messageId: userMessageId 
          }))
          throw error
        }
      }

    } catch (error) {
      console.error('Failed to send message:', error)
      throw error
    }
  }, [
    activeSessionId, 
    dispatch, 
    preferences, 
    streaming.enabled, 
    webSocket
  ])

  // Cancel streaming message
  const cancelMessage = useCallback((messageId: string) => {
    webSocket.cancelMessage(messageId)
    dispatch(cancelStreaming({ messageId }))
  }, [webSocket, dispatch])

  // Load a specific chat session
  const loadSession = useCallback(async (sessionId: string) => {
    try {
      await dispatch(loadChatSession(sessionId)).unwrap()
      dispatch(setActiveSession(sessionId))
    } catch (error) {
      console.error('Failed to load session:', error)
      throw error
    }
  }, [dispatch])

  // Start a new chat (don't leave temp sessions)
  const startNewChat = useCallback(() => {
    if (activeSessionId && 
        !activeSessionId.startsWith('temp-session-') && 
        webSocket.isConnected()) {
      webSocket.leaveSession(activeSessionId)
    }
    dispatch(setActiveSession(null))
  }, [activeSessionId, dispatch]) // Remove webSocket dependency

  // Send typing indicator
  const sendTyping = useCallback((isTyping: boolean) => {
    if (activeSessionId && webSocket.isConnected()) {
      webSocket.sendTyping(isTyping, activeSessionId)
    }
  }, [activeSessionId]) // Remove webSocket from dependencies

  // Connection management
  const connect = useCallback(async () => {
    try {
      await webSocket.connect()
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
      throw error
    }
  }, []) // Remove webSocket dependency

  const disconnect = useCallback(() => {
    webSocket.disconnect()
  }, []) // Remove webSocket dependency

  // Get current session state - computed every render to ensure updates
  const getSessionState = useCallback(() => {
    if (!activeSession) {
      return {
        session: null,
        messages: [],
        isLoading: false,
        hasActiveStreams: false,
        typingUsers: []
      }
    }

    // Use persisted messages as the primary source since they now get updated with streaming content
    const persistedMessages = activeSession.messages || []
    
    // Only include streaming messages that don't have a corresponding persisted message
    const persistedMessageIds = new Set(persistedMessages.map(msg => msg.id))
    const currentStreamingMessages = streamingMessages.streamingMessages
      .filter(msg => msg.sessionId === activeSessionId && !persistedMessageIds.has(msg.id))
      .map(streamMsg => ({
        id: streamMsg.id,
        role: 'ASSISTANT' as const,
        content: streamMsg.content,
        createdAt: streamMsg.timestamp,
        isStreaming: streamMsg.isStreaming,
        error: streamMsg.hasError ? 'Error generating response' : undefined,
        sources: streamMsg.citations.length > 0 ? streamMsg.citations.map(citation => ({
          id: citation.documentId,
          chunkId: citation.documentId,
          relevanceScore: citation.relevanceScore,
          wasUsed: true,
          citationText: citation.snippet,
          rankOrder: 1,
          chunk: {
            id: citation.documentId,
            text: citation.snippet,
            document: {
              id: citation.documentId,
              title: citation.documentTitle,
              filename: citation.documentTitle,
              fileType: 'pdf' as const
            }
          }
        })) : undefined
      }))

    // Combine and sort messages by timestamp to ensure proper order
    const allMessages = [...persistedMessages, ...currentStreamingMessages]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    
    // Debug: getSessionState calculations
    // console.log('🔍 getSessionState debug:', { activeSessionId, persistedCount: persistedMessages.length, streamingCount: currentStreamingMessages.length, totalMessages: allMessages.length })

    return {
      session: activeSession,
      messages: allMessages,
      isLoading: activeSession.isLoading,
      hasActiveStreams: streamingMessages.hasActiveStreams,
      typingUsers: streamingMessages.typingUsers
    }
  }, [activeSession, activeSessionId, streamingMessages])

  return {
    // State
    sessions: Object.values(sessions),
    activeSession,
    activeSessionId,
    preferences,
    isInitialized,
    loadingSessions,
    sessionsError,
    
    // Streaming state
    streamingEnabled: streaming.enabled,
    currentStreamingMessage: streaming.currentStreamingMessage,
    hasActiveStreams: streamingMessages.hasActiveStreams,
    typingUsers: streamingMessages.typingUsers,
    
    // Connection state
    isConnected: webSocket.isConnected(),
    
    // Actions
    sendMessage,
    cancelMessage,
    loadSession,
    startNewChat,
    sendTyping,
    connect,
    disconnect,
    
    // Utilities
    getSessionState
  }
}

export default useHRChatStreaming